/**
 * Workflow engine — credit-spend descriptors and the static estimate.
 *
 * Client-safe by construction. The only import outside this folder is
 * `@repo/payments/client`, which is the `credit-costs.ts` config module: pure
 * `NEXT_PUBLIC_*`-backed constants and pure helpers, with no database and no
 * `server-only` in its graph. The server-side composition — the one that reads
 * a plan's remaining allocation and the org's balance — lives in
 * `spend-server.ts` and never reaches a browser bundle.
 *
 * The split matters because the two answer different questions:
 *
 * - **Static** (`estimateWorkflowSpendStatic`) answers "what does this playbook
 *   cost, in general" from constants alone. It is what a library card can say
 *   without a round trip, for every card on the page at once.
 * - **Live** (`estimateWorkflowSpend`) answers "what will this cost *me*, right
 *   now" and needs the org's plan, its allocations and its balance. One call,
 *   on the runner, before the user starts.
 *
 * Per-unit kinds (a prompt, a page, a competitor, a prospect) contribute their
 * *unit* cost to the static minimum exactly once. That is why the number is
 * presented as "from N credits" rather than as a total: the multiplier is a
 * property of the user's data, not of the playbook.
 */

import {
	AI_REPORT_CREDITS,
	CITATION_REWRITE_CREDITS,
	COMPETITOR_BRAND_CONSISTENCY_CREDITS,
	FAQ_RUN_CREDITS,
	PLACEMENT_SCORE_CREDITS,
	TRACKED_PROMPT_OVERAGE_CREDITS,
	calculateSiteAuditCredits,
} from "@repo/payments/client";
import { WORKFLOW_FEATURES } from "./catalog/features";
import type { WorkflowDefinition, WorkflowStep } from "./types";

// ── Descriptors ────────────────────────────────────────────────────────────────

/** Carried by every descriptor: one short line the panel prints under the row. */
interface WorkflowSpendNote {
	note?: string;
}

/**
 * What a step spends, declared by the catalog.
 *
 * `"fromState"` means "the quantity is a property of the user's data, not of
 * the playbook" — the server resolves it from observed state and the matching
 * budget function. A step that only *may* spend (the user could stop at the
 * free half of the page) declares `free` with a note rather than a cost, so the
 * panel never quotes a number the user is not committed to.
 */
export type WorkflowSpendDescriptor =
	/** One AI Visibility Report per `count` (default 1). */
	| ({ kind: "aiReport"; count?: number } & WorkflowSpendNote)
	/**
	 * Tracked prompts added to a project. `"fromState"` estimates from the
	 * property's active prompts; the precise number — allocation, location
	 * fan-out, dedupe — comes from `getTrackedPromptBudget`.
	 */
	| ({
			kind: "trackedPrompts";
			count: number | "fromState";
	  } & WorkflowSpendNote)
	/** A site audit of `pages` pages; `"fromState"` reads the property's page count. */
	| ({ kind: "siteAudit"; pages: number | "fromState" } & WorkflowSpendNote)
	/** Citation Optimizer rewrites. Scoring is free — only a rewrite bills. */
	| ({ kind: "citationRewrite"; count?: number } & WorkflowSpendNote)
	/** FAQ generation runs (flat per run, whatever the FAQ count). */
	| ({ kind: "faqRun"; count?: number } & WorkflowSpendNote)
	/** Brand consistency check: one credit per tracked competitor. */
	| ({ kind: "competitorConsistency" } & WorkflowSpendNote)
	/** Bulk prospecting: one AI Visibility Report per prospect. */
	| ({ kind: "prospectReports"; count?: number } & WorkflowSpendNote)
	/**
	 * Placement quality scores. Priced at zero today (billed by contract, not
	 * in-app), so this contributes nothing to the floor — it is declared so the
	 * step says what it consumes, and so pricing it later is a config change.
	 * `"fromState"` means the quantity is the user's list, not the playbook's.
	 */
	| ({
			kind: "placementScore";
			count: number | "fromState";
	  } & WorkflowSpendNote)
	/** Costs nothing. The note says what the reader was probably worried about. */
	| { kind: "free"; note: string };

export type WorkflowSpendKind = WorkflowSpendDescriptor["kind"];

/** One row of the "Before you start" panel: a step, and what it will cost. */
export interface WorkflowSpendLine {
	stepId: string;
	stepTitle: string;
	kind: WorkflowSpendKind;
	/** Credits this step will debit. 0 when free or covered by the plan. */
	credits: number;
	/** True when the plan's allocation absorbs it — free, but not "Free". */
	includedInPlan: boolean;
	/** True when the quantity is inferred rather than committed to. */
	estimated: boolean;
	note?: string;
}

/**
 * The live estimate, as it crosses the wire. The *type* is client-safe — the
 * function that produces it (`estimateWorkflowSpend`) is not, and stays in
 * `spend-server.ts`.
 */
export interface WorkflowSpendEstimate {
	lines: WorkflowSpendLine[];
	/** Sum of `lines[].credits`. Zero for a playbook that only spends the plan. */
	totalCredits: number;
	availableBalance: number;
	/** Alias kept for the panel's plainer reading of the same number. */
	balance: number;
	canAfford: boolean;
	isPayAsYouGo: boolean;
	/** The org's plan type, or null on pay-as-you-go. */
	planLabel: string | null;
	/** True when any line's quantity is inferred rather than committed to. */
	estimated: boolean;
}

export interface WorkflowSpendStatic {
	/** Sum of unit costs — the floor, not a total. */
	minCredits: number;
	/**
	 * The part of `minCredits` that is a real, committed charge: a report, a
	 * rewrite, an FAQ run, a prospect report. Everything else in the floor is
	 * either an allowance the plan already covers (tracked prompts) or a
	 * multiplier the playbook cannot know. A library card only claims a cost
	 * when this is above zero — see `hasWorkflowSpendCardLine`.
	 */
	fixedCredits: number;
	/** At least one kind whose cost multiplies by the user's data. */
	hasPerUnit: boolean;
	kinds: WorkflowSpendKind[];
	/** Nothing in the playbook bills. Hides the panel entirely. */
	freeOnly: boolean;
}

/**
 * Kinds that debit credits outright rather than drawing on a plan allowance.
 *
 * `trackedPrompts` is the one deliberately left out: on every plan that has an
 * allocation it costs nothing until the allocation runs out, so quoting it on a
 * card taught users that a playbook they can already run has a price.
 */
const FIXED_SPEND_KINDS = [
	"aiReport",
	"citationRewrite",
	"faqRun",
	"prospectReports",
	// Zero today, so it adds nothing to a card's quoted cost — but it is a
	// straight per-action charge the moment it is priced, not an allowance.
	"placementScore",
] as const satisfies ReadonlyArray<WorkflowSpendKind>;

function isFixedSpendKind(kind: WorkflowSpendKind): boolean {
	return (FIXED_SPEND_KINDS as ReadonlyArray<WorkflowSpendKind>).includes(
		kind,
	);
}

// ── Unit costs ─────────────────────────────────────────────────────────────────

/**
 * The credits one *unit* of a descriptor costs at list price, ignoring plan
 * allocations. `perUnit` says whether the real cost multiplies by something
 * the playbook cannot know (prompts tracked, pages audited, competitors,
 * prospects imported).
 */
export function workflowSpendUnitCost(descriptor: WorkflowSpendDescriptor): {
	credits: number;
	perUnit: boolean;
} {
	switch (descriptor.kind) {
		// A report, a rewrite and an FAQ run are each a single committed
		// action: an absent `count` means one, not "unknown".
		case "aiReport":
			return {
				credits: AI_REPORT_CREDITS * (descriptor.count ?? 1),
				perUnit: false,
			};
		case "citationRewrite":
			return {
				credits: CITATION_REWRITE_CREDITS * (descriptor.count ?? 1),
				perUnit: false,
			};
		case "faqRun":
			return {
				credits: FAQ_RUN_CREDITS * (descriptor.count ?? 1),
				perUnit: false,
			};
		case "trackedPrompts":
			return descriptor.count === "fromState"
				? { credits: TRACKED_PROMPT_OVERAGE_CREDITS, perUnit: true }
				: {
						credits:
							TRACKED_PROMPT_OVERAGE_CREDITS *
							Math.max(0, descriptor.count),
						perUnit: false,
					};
		case "siteAudit":
			return descriptor.pages === "fromState"
				? { credits: calculateSiteAuditCredits(1), perUnit: true }
				: {
						credits: calculateSiteAuditCredits(descriptor.pages),
						perUnit: false,
					};
		case "competitorConsistency":
			return {
				credits: COMPETITOR_BRAND_CONSISTENCY_CREDITS,
				perUnit: true,
			};
		case "placementScore":
			return descriptor.count === "fromState"
				? { credits: PLACEMENT_SCORE_CREDITS, perUnit: true }
				: {
						credits:
							PLACEMENT_SCORE_CREDITS *
							Math.max(0, descriptor.count),
						perUnit: false,
					};
		case "prospectReports":
			return {
				credits: AI_REPORT_CREDITS * (descriptor.count ?? 1),
				perUnit: descriptor.count === undefined,
			};
		case "free":
			return { credits: 0, perUnit: false };
	}
}

/**
 * Steps a user can actually reach: a step gated behind an unbuilt feature is
 * hidden from the stepper, so quoting its cost would bill for a step nobody can
 * take. Same filter `evaluateWorkflow` applies to the denominator.
 */
export function spendingSteps(
	definition: WorkflowDefinition,
): Array<{ step: WorkflowStep; spend: WorkflowSpendDescriptor }> {
	return definition.steps.flatMap((step) => {
		if (!step.spend) {
			return [];
		}
		if (step.requires && !WORKFLOW_FEATURES[step.requires]) {
			return [];
		}
		return [{ step, spend: step.spend }];
	});
}

/**
 * What a library card can say without asking the server: the floor price, the
 * kinds involved, and whether the real number scales with the user's data.
 */
export function estimateWorkflowSpendStatic(
	definition: WorkflowDefinition,
): WorkflowSpendStatic {
	const entries = spendingSteps(definition);
	const kinds: WorkflowSpendKind[] = [];
	let minCredits = 0;
	let fixedCredits = 0;
	let hasPerUnit = false;

	for (const { spend } of entries) {
		if (!kinds.includes(spend.kind)) {
			kinds.push(spend.kind);
		}
		const unit = workflowSpendUnitCost(spend);
		minCredits += unit.credits;
		if (isFixedSpendKind(spend.kind)) {
			fixedCredits += unit.credits;
		}
		hasPerUnit = hasPerUnit || unit.perUnit;
	}

	return {
		minCredits,
		fixedCredits,
		hasPerUnit,
		kinds,
		freeOnly: minCredits === 0,
	};
}
